import { useEffect, useState } from 'react';
import { financialsService } from '../../services/financials.service';

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 100;
// Bounded pagination — the gateway has no server-side date-range filter yet,
// so we page through transactions up to this cap and bucket/sum them
// client-side (both for the period chart and the lifetime "to date" totals).
// Revisit once a dateFrom/dateTo param exists upstream.
const MAX_PAGES = 10;

// Only approved transactions are money that actually moved. Pending, in-review,
// rejected, cancelled and expired intents are not counted — this matches the
// "Approved" figures on the Cash In/Out page.
export const SETTLED_STATUSES = new Set(['APPROVED']);
const isSettled = (t) => SETTLED_STATUSES.has(t?.status);

async function fetchAllPages(listFn) {
  let items = [];
  let cursor;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await listFn({ limit: PAGE_SIZE, cursor });
    const pageItems = Array.isArray(result?.items) ? result.items : [];
    items = items.concat(pageItems);
    cursor = result?.nextCursor || null;
    if (!cursor) break;
  }
  return items;
}

export const TRANSACTION_RANGES = [
  { value: '7d', label: '7D', days: 7 },
  { value: '30d', label: '30D', days: 30 },
  { value: '90d', label: '90D', days: 90 },
];

// Real deposits/withdrawals fetched from platform-financials-service via the
// partner gateway — the same endpoints already used by the Cash In/Out page.
export const useCashMovementData = () => {
  const [state, setState] = useState({ loading: true, deposits: [], withdrawals: [] });

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchAllPages(financialsService.listDeposits).catch(() => []),
      fetchAllPages(financialsService.listWithdrawals).catch(() => []),
    ]).then(([deposits, withdrawals]) => {
      if (!active) return;
      setState({ loading: false, deposits, withdrawals });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
};

/**
 * Currency the cash-movement card should report in: the preferred currency
 * when there is approved money in it, otherwise the currency holding the most
 * approved money, otherwise the preferred currency. Keeps the label and the
 * figures in the same, real currency (the org's base-currency setting can
 * differ from the currency customers actually fund in).
 */
export const pickCashCurrency = (deposits, withdrawals, preferred = 'AED') => {
  const totals = new Map();
  [...(deposits || []), ...(withdrawals || [])].forEach((t) => {
    if (!isSettled(t) || !t.currency) return;
    totals.set(t.currency, (totals.get(t.currency) || 0) + (Number.parseFloat(t.amount) || 0));
  });
  if (totals.has(preferred)) return preferred;
  let best = null;
  totals.forEach((amount, currency) => {
    if (!best || amount > best.amount) best = { currency, amount };
  });
  return best ? best.currency : preferred;
};

export const hasSettledTransactions = (deposits, withdrawals, currency) =>
  [...(deposits || []), ...(withdrawals || [])].some((t) => isSettled(t) && t.currency === currency);

const dayKey = (isoString) => new Date(isoString).toISOString().slice(0, 10);

// Builds a daily series for the org's base currency only — summing across
// currencies would misrepresent real money, so the hero chart stays honest
// by scoping to a single currency and letting the currency breakdown card
// show the rest.
export const buildDailySeries = (deposits, withdrawals, days, baseCurrency) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets = new Map();
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(today.getTime() - i * DAY_MS);
    buckets.set(date.toISOString().slice(0, 10), {
      date: date.toISOString().slice(0, 10),
      label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      deposits: 0,
      withdrawals: 0,
    });
  }

  const accumulate = (items, key) => {
    items.forEach((t) => {
      if (t.currency !== baseCurrency) return;
      if (!isSettled(t)) return;
      const bucket = buckets.get(dayKey(t.createdAt));
      if (!bucket) return;
      bucket[key] += Number.parseFloat(t.amount) || 0;
    });
  };
  accumulate(deposits, 'deposits');
  accumulate(withdrawals, 'withdrawals');

  return Array.from(buckets.values()).map((b) => ({ ...b, volume: b.deposits + b.withdrawals }));
};

// Lifetime "to date" totals — unlike buildDailySeries, this isn't scoped to
// a range toggle; it's every fetched transaction, so cumulative figures don't
// silently shrink when someone picks a shorter chart window.
export const buildLifetimeTotals = (deposits, withdrawals, baseCurrency) => {
  const sum = (items) =>
    items.reduce((total, t) => {
      if (t.currency !== baseCurrency) return total;
      if (!isSettled(t)) return total;
      return total + (Number.parseFloat(t.amount) || 0);
    }, 0);

  const totalDeposited = sum(deposits);
  const totalWithdrawn = sum(withdrawals);
  return { totalDeposited, totalWithdrawn, totalProcessed: totalDeposited + totalWithdrawn };
};

// Groups real deposits by currency (no cross-currency conversion — each
// currency's total stands on its own).
export const buildCurrencyBreakdown = (deposits) => {
  const totals = new Map();
  deposits.forEach((t) => {
    if (!isSettled(t)) return;
    const amount = Number.parseFloat(t.amount) || 0;
    totals.set(t.currency, (totals.get(t.currency) || 0) + amount);
  });
  return Array.from(totals.entries())
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
};
